/*
 * useWaiterCallsRealtime
 *
 * Realtime listener for the POS / waiter-app waiter-call dashboard. Subscribes
 * to the per-restaurant Reverb/Pusher channel and (optionally) the per-staff
 * personal channel so both
 *  • all calls fired across the restaurant, and
 *  • calls whose status changed (acknowledged / resolved)
 * arrive instantly without polling.
 *
 * Channels (per docs/QR_WAITER_CALL.md §3):
 *   restaurant.{rid}.waiter-calls                                   — POS dashboard
 *   restaurant.{rid}.staff.{userId}.waiter-calls                    — personal waiter
 *
 * Events listened on (broadcastAs names):
 *   .waiter.called                  → relayed via `onCalled`
 *   .waiter-call.status-changed     → relayed via `onStatusChanged`
 *
 * On websocket disconnect we fall back to polling the parent's `onRefresh`
 * callback every `pollIntervalMs` (default 12s — slightly longer than the
 * customer-side hook because the staff list is more expensive to render).
 */

import { useCallback, useEffect, useRef } from 'react';
import echo from '../services/echo';
import { QrWaiterCalledEvent, QrWaiterCallStatusChangedEvent } from '../api/qrWaiterCall';

export interface UseWaiterCallsRealtimeOptions {
  /** Restaurant id from `localStorage.restaurant_info`. Hook is a no-op without it. */
  restaurantId: number | null | undefined;
  /**
   * Optional staff user id. When supplied the hook ALSO subscribes to the
   * personal `restaurant.{rid}.staff.{userId}.waiter-calls` channel so the
   * waiter still gets nudges if the public channel is partitioned/private.
   */
  staffUserId?: number | null;
  /** Fired when a brand-new call lands in the restaurant. */
  onCalled: (event: QrWaiterCalledEvent) => void;
  /** Fired when an existing call moves pending → acknowledged → resolved. */
  onStatusChanged: (event: QrWaiterCallStatusChangedEvent) => void;
  /**
   * Called on websocket (re)connect AND on every poll tick when the
   * websocket is unavailable. Parent should refetch the canonical list.
   */
  onRefresh: () => void;
  /** Polling interval used when the websocket is down. Default 12s. */
  pollIntervalMs?: number;
  /** Disable the hook entirely (e.g. when no token / role mismatch). */
  disabled?: boolean;
}

export function useWaiterCallsRealtime(opts: UseWaiterCallsRealtimeOptions): void {
  const {
    restaurantId,
    staffUserId = null,
    onCalled,
    onStatusChanged,
    onRefresh,
    pollIntervalMs = 12000,
    disabled = false,
  } = opts;

  // Stable refs so the parent's lambdas don't re-trigger the effect.
  const onCalledRef = useRef(onCalled);
  const onStatusChangedRef = useRef(onStatusChanged);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onCalledRef.current = onCalled;
  }, [onCalled]);
  useEffect(() => {
    onStatusChangedRef.current = onStatusChanged;
  }, [onStatusChanged]);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const handleCalled = useCallback((payload: any) => {
    if (!payload || typeof payload !== 'object') return;
    onCalledRef.current(payload as QrWaiterCalledEvent);
  }, []);

  const handleStatusChanged = useCallback((payload: any) => {
    if (!payload || typeof payload !== 'object') return;
    onStatusChangedRef.current(payload as QrWaiterCallStatusChangedEvent);
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (!restaurantId) return;

    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        onRefreshRef.current();
      }, pollIntervalMs);
    };
    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // ---------- Subscribe to channels ----------
    const channelNames: string[] = [];

    const subscribeChannel = (name: string) => {
      try {
        const ch = echo.channel(name);
        // The backend uses `broadcastAs(...)` so events come over the
        // dotted name regardless of the PHP class.
        ch.listen('.waiter.called', handleCalled);
        ch.listen('.waiter-call.status-changed', handleStatusChanged);
        channelNames.push(name);
      } catch {
        /* fall through to polling */
      }
    };

    subscribeChannel(`restaurant.${restaurantId}.waiter-calls`);
    if (staffUserId) {
      subscribeChannel(`restaurant.${restaurantId}.staff.${staffUserId}.waiter-calls`);
    }

    // ---------- Connection state -> polling fallback ----------
    const connector = (echo as any).connector?.pusher;
    const onConnected = () => {
      stopPolling();
      // Reconciliation pull on (re)connect — covers the gap during the
      // disconnect when broadcast events were missed.
      onRefreshRef.current();
    };
    const onDisconnected = () => {
      startPolling();
    };

    if (connector?.connection) {
      connector.connection.bind('connected', onConnected);
      connector.connection.bind('disconnected', onDisconnected);
      connector.connection.bind('unavailable', onDisconnected);
      connector.connection.bind('failed', onDisconnected);
      if (connector.connection.state !== 'connected') {
        startPolling();
      }
    } else {
      // No websocket transport at all — pure polling.
      startPolling();
    }

    return () => {
      stopPolling();
      channelNames.forEach((name) => {
        try {
          echo.leaveChannel(name);
        } catch {
          /* ignore */
        }
      });
      if (connector?.connection) {
        try {
          connector.connection.unbind('connected', onConnected);
          connector.connection.unbind('disconnected', onDisconnected);
          connector.connection.unbind('unavailable', onDisconnected);
          connector.connection.unbind('failed', onDisconnected);
        } catch {
          /* ignore */
        }
      }
    };
  }, [restaurantId, staffUserId, pollIntervalMs, disabled, handleCalled, handleStatusChanged]);
}
